import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useEffect,
} from "react";
import { useTranslation } from "react-i18next";

export type SummaryCardHandle = {
  /** return current draft summary text */
  getDraft: () => string;
  /** set draft summary programmatically (e.g., when loading) */
  setDraft: (s: string) => void;
};

type Props = {
  busy?: boolean;
  justSent?: boolean;

  /** include pinned bubbles when auto-summarizing (now forced true) */
  includePinned: boolean;
  setIncludePinned: (v: boolean) => void;

  summary: string;
  setSummary: (s: string) => void;

  /** summarize action */
  onSummarize?: () => void;
};

/** Simple stroke icon to match the rest of the UI */
const SparklesIcon: React.FC<React.SVGProps<SVGSVGElement>> = (p) => (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...p}
  >
    <path d="M12 3l1.6 3.7L17 8.3l-3.7 1.6L12 13l-1.3-3.1L7 8.3l3.7-1.6L12 3z" />
    <path d="M18.5 14l.9 2.1 2.1.9-2.1.9-.9 2.1-.9-2.1-2.1-.9 2.1-.9.9-2.1z" />
    <path d="M5 14l.7 1.6L7.3 16l-1.6.7L5 18.3l-.7-1.6L2.7 16l1.6-.7L5 14z" />
  </svg>
);

/** Minimal hourglass icon to signal "running" */
const HourglassIcon: React.FC<React.SVGProps<SVGSVGElement>> = (p) => (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...p}
  >
    <path d="M6 2h12M6 22h12" />
    <path d="M6 2c0 5 5 6 6 10 1-4 6-5 6-10" />
    <path d="M6 22c0-5 5-6 6-10 1 4 6 5 6 10" />
  </svg>
);

/** Helper: today's date in YYYY-MM-DD (local) */
function todayISO(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/** Helper: basic YYYY-MM-DD validator */
function isISODate(s: string | undefined | null): boolean {
  return !!s && /^\d{4}-\d{2}-\d{2}$/.test(s);
}

export const SummaryCard = forwardRef<SummaryCardHandle, Props>(
  (
    {
      busy,
      justSent,
      includePinned,
      setIncludePinned,
      summary,
      setSummary,
      onSummarize,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const draftRef = useRef<string>(summary ?? "");

    // Force "include pinned" to be true and keep it true
    useEffect(() => {
      if (!includePinned) setIncludePinned(true);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [includePinned]);

    // Keep internal draft mirror in sync with controlled summary
    useEffect(() => {
      draftRef.current = summary ?? "";
    }, [summary]);

    useImperativeHandle(
      ref,
      () => ({
        getDraft: () => draftRef.current,
        setDraft: (s: string) => {
          draftRef.current = s ?? "";
          setSummary(draftRef.current);
        },
      }),
      [setSummary]
    );

    // Controlled text area
    const onChangeText: React.ChangeEventHandler<HTMLTextAreaElement> = (e) => {
      draftRef.current = e.target.value;
      setSummary(e.target.value);
    };

    const isEmpty = !summary || summary.trim() === "";

    return (
      <div className={`spn-card spn-summary ${busy ? "is-busy" : ""}`}>
        <div className="spn-summary-body">
          {isEmpty && !busy && (
            <div className="spn-empty-state">
              <p>{t("session.summaryEmptyState").split("\n").map((line, i) => (
                <React.Fragment key={i}>{line}{i === 0 && <br />}</React.Fragment>
              ))}</p>
            </div>
          )}
          <textarea
            className={`spn-textarea spn-summary-textarea ${isEmpty ? "spn-textarea--empty" : ""}`}
            value={summary || ""}
            onChange={onChangeText}
            style={{ opacity: busy ? 0.6 : 1 }}
          />
        </div>

        {justSent && <div className="spn-toast ok">{t("session.sentToWorkspace")}</div>}
      </div>
    );
  }
);

export default SummaryCard;
